import React from "react";
import { formatRelativeTime, formatAbsolute, formatCalendarDate, parseBackendDate } from "../utils/date";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Stack,
  CircularProgress,
  IconButton,
  Drawer,
  Divider,
  Tooltip,
  LinearProgress,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { alpha, useTheme, type SxProps, type Theme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { AppDialog, ConfirmDialog } from "../components/dialog";
import AddCircleOutlineRounded from "@mui/icons-material/AddCircleOutlineRounded";
import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import EditRounded from "@mui/icons-material/EditRounded";
import DashboardOutlined from "@mui/icons-material/DashboardOutlined";
import ArchiveRounded from "@mui/icons-material/ArchiveRounded";
import UnarchiveRounded from "@mui/icons-material/UnarchiveRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import AutoAwesomeRounded from "@mui/icons-material/AutoAwesomeRounded";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import LabelRounded from "@mui/icons-material/LabelRounded";
import ChatBubbleOutlineRounded from "@mui/icons-material/ChatBubbleOutlineRounded";
import EditNoteRounded from "@mui/icons-material/EditNoteRounded";
import BuildRounded from "@mui/icons-material/BuildRounded";
import HelpOutlineRounded from "@mui/icons-material/HelpOutlineRounded";
import SaveRounded from "@mui/icons-material/SaveRounded";
import { buildApiUrl, tenantApiPrefix } from "../config";
import { getTenantIdFromStorage } from "../utils/onboardingLocalStorage";
import { SnackbarContext } from "../ui/SnackbarProvider";
import {
  DownloadButton,
  ListTable,
  MetricCard,
  PageHeader,
  Panel,
  SearchField,
  SegmentedControl,
  ToolbarButton,
  type ListColumn,
} from "../ui-kit";

// ============================================================================
// Types
// ============================================================================

interface KnowledgeNode {
  node_id: string;
  node_type: string;
  name: string;
  content: Record<string, any>;
  source_type: string;
  confidence: number;
  validation_count: number;
  scope: string;
  scope_user_id: string | null;
  last_validated: string | null;
  last_used: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  /** Where the definition came from: a dashboard, an admin, or a chat. */
  definition_type?: DefinitionType | null;
  /** For dashboard rows, which dashboard defines it. Shape set by the backend. */
  origin?: string | Record<string, any> | null;
  created_by_name?: string | null;
  updated_by_name?: string | null;
}

type DefinitionType = "dashboard" | "admin" | "chat";
type DefinitionView = "all" | DefinitionType;

const DEFINITION_VIEWS: { value: DefinitionView; label: string }[] = [
  { value: "all", label: "All" },
  { value: "dashboard", label: "From dashboards" },
  { value: "admin", label: "Entered by an admin" },
  { value: "chat", label: "Learned in chat" },
];

/** The tiles at the top: every definition, then one per source. */
const SOURCE_TILES: { view: DefinitionView; label: string; info: string }[] = [
  { view: "all", label: "All definitions", info: "Every definition Copilot can use, from every source." },
  { view: "dashboard", label: "From dashboards", info: "Read from your dashboards and always kept in sync with them." },
  { view: "admin", label: "Entered by an admin", info: "Added here or imported by an admin. They win over definitions saved from a chat." },
  { view: "chat", label: "Learned in chat", info: "Saved from a chat by someone in your team." },
];

/**
 * Who added a definition. Chat definitions with no one recorded were saved
 * by Copilot on its own, before a person had to approve each save.
 */
function createdBy(node: KnowledgeNode): string {
  if (node.created_by_name) return node.created_by_name;
  if (node.definition_type === "chat") return "Copilot";
  return "—";
}

/** Dashboard definitions are changed in the dashboard, never here. */
function isFromDashboard(node: KnowledgeNode): boolean {
  return node.definition_type === "dashboard";
}

function dashboardName(node: KnowledgeNode): string | null {
  const o = node.origin;
  if (!o || typeof o === "string") return null;
  const name = o.board || o.dashboard || o.board_name || o.dashboard_name;
  return typeof name === "string" && name ? name : null;
}

function formatDay(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = parseBackendDate(iso);
  if (!d || Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** One plain line on where a definition came from, or null when there is nothing to add. */
function originLine(node: KnowledgeNode): string | null {
  if (node.definition_type === "dashboard") {
    const board = dashboardName(node);
    return board ? `From the ${board} dashboard` : "From a dashboard";
  }
  if (node.definition_type === "chat") {
    const by = node.created_by_name ? ` by ${node.created_by_name}` : "";
    const on = formatDay(node.created_at);
    let line = `Saved from a chat${by}${on ? ` on ${on}` : ""}`;
    if (node.updated_by_name && node.updated_at && node.updated_at !== node.created_at) {
      line += ` · edited by ${node.updated_by_name} on ${formatDay(node.updated_at)}`;
    }
    return line;
  }
  if (node.definition_type === "admin" && node.created_by_name) {
    return `Entered by ${node.created_by_name}`;
  }
  return null;
}

interface KnowledgeStats {
  total: number;
  by_type: Record<string, number>;
  by_source: Record<string, number>;
  recent_count: number;
  by_definition_type?: Record<string, number>;
}

interface ChangelogEntry {
  changelog_id: string;
  action: string;
  actor_type: string;
  actor_id: string | null;
  previous_value: Record<string, any> | null;
  new_value: Record<string, any> | null;
  created_at: string;
}


// ============================================================================
// Type / source meta — single source of truth, drives chips, dropdowns, icons
// ============================================================================

type Tone = "info" | "warning" | "neutral" | "success";


interface SourceMeta {
  label: string;
  // Component reference (not a constructed element) so callers can pass `sx`
  // and TypeScript can type-check it. Keeps icons sizable per usage instead
  // of locked to a single size when stored as a JSX element.
  Icon: React.ComponentType<{ sx?: SxProps<Theme> }>;
}

// Order matters — used for the Source filter dropdown.
const SOURCE_META: Record<string, SourceMeta> = {
  chat_definition: { label: "Chat", Icon: ChatBubbleOutlineRounded },
  manual: { label: "Admin", Icon: EditNoteRounded },
  dashboard: { label: "Dashboard", Icon: DashboardOutlined },
  sql_correction: { label: "SQL correction", Icon: BuildRounded },
  inferred: { label: "Inferred", Icon: AutoAwesomeRounded },
  clarification: { label: "Clarification", Icon: HelpOutlineRounded },
};

// ============================================================================
// Helpers
// ============================================================================

function useAuthHeaders() {
  return React.useMemo(() => {
    const token = window.localStorage.getItem("klaris_jwt");
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
  }, []);
}

/** Debounce a value by `ms` milliseconds. */
function useDebouncedValue<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

function relativeTime(iso: string | null): string {
  if (!iso) return "—";
  return formatRelativeTime(iso);
}


/** Friendly label for a changelog actor. */
function formatActor(actor_type: string | null | undefined): string {
  switch ((actor_type || "").toLowerCase()) {
    case "user":
      return "You";
    case "agent":
    case "ai":
    case "system":
      return "AI";
    default:
      return actor_type || "—";
  }
}

/** Day bucket label for grouping history. */
function dayBucket(input: string): string {
  const d = parseBackendDate(input);
  if (!d) return "Unknown";
  const today = new Date();
  const yest = new Date();
  yest.setDate(today.getDate() - 1);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(d, today)) return "Today";
  if (sameDay(d, yest)) return "Yesterday";
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: d.getFullYear() === today.getFullYear() ? undefined : "numeric" }).format(d);
}

/**
 * Walk previous_value vs new_value and produce a list of human-readable
 * change descriptors. We surface the fields the UI exposes (name,
 * description, sql, node_type) — anything else is folded into a generic
 * "other fields".
 */
function summarizeChange(entry: ChangelogEntry): string[] {
  const prev = entry.previous_value || {};
  const next = entry.new_value || {};
  const out: string[] = [];

  const renameFromAction = (entry.action || "").toLowerCase();
  if (renameFromAction === "create") {
    out.push("Created");
    return out;
  }
  if (renameFromAction === "archive") {
    out.push("Archived");
    return out;
  }
  if (renameFromAction === "unarchive" || renameFromAction === "restore") {
    out.push("Restored");
    return out;
  }

  if (prev.name !== undefined && next.name !== undefined && prev.name !== next.name) {
    out.push(`Renamed: "${prev.name}" → "${next.name}"`);
  }
  const prevDef = prev.description ?? prev.content?.description;
  const nextDef = next.description ?? next.content?.description;
  if (prevDef !== undefined && nextDef !== undefined && prevDef !== nextDef) {
    out.push("Definition updated");
  }
  const prevSql = prev.sql ?? prev.content?.sql ?? prev.content?.formula_sql ?? prev.content?.rule_sql;
  const nextSql = next.sql ?? next.content?.sql ?? next.content?.formula_sql ?? next.content?.rule_sql;
  if (prevSql !== undefined && nextSql !== undefined && prevSql !== nextSql) {
    out.push("SQL updated");
  }
  if (prev.node_type !== undefined && next.node_type !== undefined && prev.node_type !== next.node_type) {
    out.push(`Type: ${prev.node_type} → ${next.node_type}`);
  }

  if (out.length === 0) {
    out.push((entry.action || "Updated").replace(/_/g, " "));
  }
  return out;
}

// ============================================================================
// Main page
// ============================================================================

export default function KnowledgeBasePage() {
  const { notify } = React.useContext(SnackbarContext);
  const headers = useAuthHeaders();

  // Data
  const [nodes, setNodes] = React.useState<KnowledgeNode[]>([]);
  const [stats, setStats] = React.useState<KnowledgeStats | null>(null);
  const [loading, setLoading] = React.useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = React.useState<string>("");
  const debouncedSearch = useDebouncedValue(searchQuery, 250);
  const [showArchived, setShowArchived] = React.useState(false);
  const [definitionView, setDefinitionView] = React.useState<DefinitionView>("all");


  // Detail panel
  const [selectedNode, setSelectedNode] = React.useState<KnowledgeNode | null>(null);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [changelog, setChangelog] = React.useState<ChangelogEntry[]>([]);
  const [changelogLoading, setChangelogLoading] = React.useState(false);

  // Edit state
  const [editing, setEditing] = React.useState(false);
  const [editName, setEditName] = React.useState("");
  const [editDefinition, setEditDefinition] = React.useState("");
  const [editFormula, setEditFormula] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  // Archive confirm
  const [archiveConfirmOpen, setArchiveConfirmOpen] = React.useState(false);
  const [archiveTarget, setArchiveTarget] = React.useState<KnowledgeNode | null>(null);
  const [archiveBusy, setArchiveBusy] = React.useState(false);

  // Add modal
  const [addOpen, setAddOpen] = React.useState(false);
  const [addName, setAddName] = React.useState("");
  const [addDefinition, setAddDefinition] = React.useState("");
  const [addFormula, setAddFormula] = React.useState("");
  const [addLoading, setAddLoading] = React.useState(false);

  // Import / export
  const [exporting, setExporting] = React.useState(false);
  const [importing, setImporting] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  // ── Fetch data ───────────────────────────────────────────────────

  // Every definition at once (a workspace has hundreds, not thousands), so the
  // table sorts, pages and searches without going back to the server.
  const fetchKnowledge = React.useCallback(async () => {
    setLoading(true);
    try {
      const tid = getTenantIdFromStorage();
      if (!tid) return;
      const params = new URLSearchParams({ limit: "1000", sort: "last_used" });
      if (showArchived) params.set("include_archived", "true");
      const resp = await fetch(
        buildApiUrl(`${tenantApiPrefix(tid)}/knowledge/nodes?${params.toString()}`),
        { headers } as RequestInit
      );
      if (!resp.ok) throw new Error("Failed to fetch");
      const data = await resp.json();
      setNodes(data.nodes || []);
      setStats(data.stats ?? null);
    } catch (e) {
      console.error("Failed to fetch knowledge base:", e);
    } finally {
      setLoading(false);
    }
  }, [headers, showArchived]);

  React.useEffect(() => {
    fetchKnowledge();
  }, [fetchKnowledge]);

  const visibleNodes = React.useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return nodes.filter(
      (n) =>
        (definitionView === "all" || n.definition_type === definitionView) &&
        (!q || n.name.toLowerCase().includes(q) || (n.content?.description || "").toLowerCase().includes(q))
    );
  }, [nodes, definitionView, debouncedSearch]);

  const countFor = (view: DefinitionView): number =>
    !stats ? 0 : view === "all" ? stats.total : stats.by_definition_type?.[view] ?? 0;

  // ── Detail panel ─────────────────────────────────────────────────

  const openDetail = React.useCallback(
    async (node: KnowledgeNode) => {
      setSelectedNode(node);
      setDetailOpen(true);
      setEditing(false);
      setChangelogLoading(true);
      try {
        const tid = getTenantIdFromStorage();
        if (!tid) return;
        const resp = await fetch(
          buildApiUrl(`${tenantApiPrefix(tid)}/knowledge/nodes/${node.node_id}/history`),
          { headers } as RequestInit
        );
        if (resp.ok) {
          setChangelog(await resp.json());
        }
      } catch {
        setChangelog([]);
      } finally {
        setChangelogLoading(false);
      }
    },
    [headers]
  );

  const closeDetail = React.useCallback(() => {
    if (saving) return;
    setDetailOpen(false);
    setEditing(false);
  }, [saving]);

  const startEdit = React.useCallback(() => {
    if (!selectedNode) return;
    setEditName(selectedNode.name);
    setEditDefinition(selectedNode.content?.description || "");
    setEditFormula(
      selectedNode.content?.sql ||
        selectedNode.content?.formula_sql ||
        selectedNode.content?.rule_sql ||
        ""
    );
    setEditing(true);
  }, [selectedNode]);

  const saveEdit = React.useCallback(async () => {
    if (!selectedNode) return;
    setSaving(true);
    try {
      const tid = getTenantIdFromStorage();
      if (!tid) return;
      const body: any = {};
      if (editName !== selectedNode.name) body.name = editName;
      if (editDefinition !== (selectedNode.content?.description || "")) body.definition = editDefinition;
      const currentSql =
        selectedNode.content?.sql ||
        selectedNode.content?.formula_sql ||
        selectedNode.content?.rule_sql ||
        "";
      if (editFormula !== currentSql) body.sql = editFormula;

      const resp = await fetch(
        buildApiUrl(`${tenantApiPrefix(tid)}/knowledge/nodes/${selectedNode.node_id}`),
        {
          method: "PUT",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify(body),
        } as RequestInit
      );
      if (!resp.ok) throw new Error("Update failed");
      const updated = await resp.json();
      setSelectedNode(updated);
      setEditing(false);
      notify("Definition updated", "success");
      fetchKnowledge();
    } catch (e) {
      notify("Failed to save changes", "error");
    } finally {
      setSaving(false);
    }
  }, [
    selectedNode,
    editName,
    editDefinition,
    editFormula,
    headers,
    notify,
    fetchKnowledge,
  ]);

  // Run the actual archive / restore call. Restore is one-click; archive
  // routes through a ConfirmDialog so users can't lose definitions by
  // misclick.
  const performArchive = React.useCallback(
    async (node: KnowledgeNode) => {
      const action = node.archived_at ? "unarchive" : "archive";
      try {
        setArchiveBusy(true);
        const tid = getTenantIdFromStorage();
        if (!tid) return;
        const resp = await fetch(
          buildApiUrl(`${tenantApiPrefix(tid)}/knowledge/nodes/${node.node_id}/${action}`),
          { method: "POST", headers } as RequestInit
        );
        if (!resp.ok) throw new Error();
        notify(action === "archive" ? "Definition deleted" : "Definition restored", "success");
        setDetailOpen(false);
        setSelectedNode(null);
        setArchiveConfirmOpen(false);
        setArchiveTarget(null);
        fetchKnowledge();
      } catch {
        notify(action === "archive" ? "We couldn’t delete this definition" : "We couldn’t bring this definition back", "error");
      } finally {
        setArchiveBusy(false);
      }
    },
    [headers, notify, fetchKnowledge]
  );

  const requestArchive = React.useCallback((node: KnowledgeNode) => {
    if (node.archived_at) {
      // Restore — no confirm needed.
      performArchive(node);
    } else {
      setArchiveTarget(node);
      setArchiveConfirmOpen(true);
    }
  }, [performArchive]);

  // ── Add definition ───────────────────────────────────────────────

  const handleAdd = React.useCallback(async () => {
    if (!addName.trim() || !addDefinition.trim()) {
      notify("Name and definition are required", "error");
      return;
    }
    setAddLoading(true);
    try {
      const tid = getTenantIdFromStorage();
      if (!tid) {
        notify("Not authenticated", "error");
        return;
      }
      const body: any = {
        name: addName.trim(),
        node_type: "metric",
        definition: addDefinition.trim(),
      };
      if (addFormula.trim()) body.sql = addFormula.trim();

      const resp = await fetch(buildApiUrl(`${tenantApiPrefix(tid)}/knowledge/nodes`), {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      } as RequestInit);
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to create");
      }
      notify("Definition created", "success");
      setAddOpen(false);
      setAddName("");
      setAddDefinition("");
      setAddFormula("");
      fetchKnowledge();
    } catch (e: any) {
      notify(e.message || "Failed to create definition", "error");
    } finally {
      setAddLoading(false);
    }
  }, [addName, addDefinition, addFormula, headers, notify, fetchKnowledge]);

  // ── Export / Import ──────────────────────────────────────────────

  const handleExport = React.useCallback(async () => {
    setExporting(true);
    try {
      const tid = getTenantIdFromStorage();
      if (!tid) {
        notify("Not authenticated", "error");
        return;
      }
      const resp = await fetch(
        buildApiUrl(`${tenantApiPrefix(tid)}/knowledge/export?format=csv&include_archived=${showArchived}`),
        { headers } as RequestInit
      );
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `knowledge_base_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      notify("Failed to export definitions", "error");
    } finally {
      setExporting(false);
    }
  }, [headers, notify, showArchived]);

  const handleImportFile = React.useCallback(
    async (file: File) => {
      setImporting(true);
      try {
        const tid = getTenantIdFromStorage();
        if (!tid) {
          notify("Not authenticated", "error");
          return;
        }
        const form = new FormData();
        form.append("file", file);
        const resp = await fetch(
          buildApiUrl(`${tenantApiPrefix(tid)}/knowledge/import`),
          { method: "POST", headers, body: form } as RequestInit
        );
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data.detail || "Import failed");
        }
        const parts: string[] = [];
        if (data.created) parts.push(`${data.created} added`);
        if (data.updated) parts.push(`${data.updated} updated`);
        if (data.skipped) parts.push(`${data.skipped} skipped`);
        const errCount = Array.isArray(data.errors) ? data.errors.length : 0;
        const summary = parts.length ? parts.join(", ") : "No changes";
        if (errCount > 0) {
          const first = data.errors[0];
          notify(
            `Imported (${summary}). ${errCount} row(s) had errors — e.g. row ${first.row}: ${first.message}`,
            "warning"
          );
        } else {
          notify(`Import complete — ${summary}`, "success");
        }
        fetchKnowledge();
      } catch (e: any) {
        notify(e.message || "Failed to import file", "error");
      } finally {
        setImporting(false);
      }
    },
    [headers, notify, fetchKnowledge]
  );

  const onFileInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = ""; // allow re-selecting the same file
      if (file) handleImportFile(file);
    },
    [handleImportFile]
  );

  const columns: ListColumn<KnowledgeNode>[] = [
    {
      key: "name",
      header: "Name",
      width: 200,
      sortValue: (n) => n.name.toLowerCase(),
      render: (n) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
            {n.name}
          </Typography>
          {n.archived_at && (
            <Typography variant="caption" color="text.secondary">
              Deleted
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: "definition",
      header: "Definition",
      grow: 1,
      minWidth: 360,
      render: (n) => (
        <Box sx={{ opacity: n.archived_at ? 0.6 : 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
            {n.content?.description || ""}
          </Typography>
          {/* Which dashboard; who and when have their own columns. */}
          {isFromDashboard(n) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
              {originLine(n)}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: "source",
      header: "Source",
      width: 110,
      sortValue: (n) => SOURCE_META[n.source_type]?.label ?? n.source_type,
      render: (n) => {
        const meta = SOURCE_META[n.source_type];
        return (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: "text.secondary" }}>
            {meta && <meta.Icon sx={{ fontSize: 14 }} />}
            <Typography variant="caption">{meta?.label ?? n.source_type}</Typography>
          </Stack>
        );
      },
    },
    {
      key: "created",
      header: "Created",
      width: 108,
      sortStartsDescending: true,
      sortValue: (n) => new Date(n.created_at).getTime(),
      render: (n) => (
        <Typography variant="caption" color="text.secondary">
          {formatCalendarDate(n.created_at)}
        </Typography>
      ),
    },
    {
      key: "updated",
      header: "Last updated",
      width: 108,
      sortStartsDescending: true,
      sortValue: (n) => new Date(n.updated_at).getTime(),
      render: (n) => (
        <Typography variant="caption" color="text.secondary">
          {formatCalendarDate(n.updated_at)}
        </Typography>
      ),
    },
    {
      key: "created_by",
      header: "Created by",
      width: 120,
      sortValue: (n) => createdBy(n).toLowerCase(),
      render: (n) => (
        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
          {createdBy(n)}
        </Typography>
      ),
    },
    {
      key: "action",
      header: "Actions",
      width: 96,
      align: "right",
      render: (n) =>
        isFromDashboard(n) ? null : (
          <Button
            size="small"
            variant="outlined"
            color={n.archived_at ? "inherit" : "error"}
            aria-label={n.archived_at ? `Restore ${n.name}` : `Delete ${n.name}`}
            onClick={(e) => {
              e.stopPropagation();
              requestArchive(n);
            }}
          >
            {n.archived_at ? "Restore" : "Delete"}
          </Button>
        ),
    },
  ];

  // ── Render ───────────────────────────────────────────────────────

  return (
    <Box>
      <PageHeader
        title="Knowledge Base"
        description="Definitions from your dashboards, entered by an admin, or saved from a chat. Copilot uses them in every answer."
        actions={
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              style={{ display: "none" }}
              onChange={onFileInputChange}
            />
            <DownloadButton
              formats={[{ key: "csv", label: "CSV", description: "Every definition: name and meaning", onDownload: handleExport }]}
              loading={exporting}
            />
            <ToolbarButton
              icon={<FileUploadOutlined fontSize="small" />}
              loading={importing}
              loadingLabel="Importing"
              tooltip="Add definitions from a CSV or Excel file with name and definition columns"
              onClick={() => fileInputRef.current?.click()}
            >
              Import
            </ToolbarButton>
            <Button variant="contained" size="small" startIcon={<AddCircleOutlineRounded />} onClick={() => setAddOpen(true)}>
              Add definition
            </Button>
          </Stack>
        }
      />

      {/* One tile per source, and all of them together. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
          gap: 1.5,
          mt: 2.5,
        }}
      >
        {SOURCE_TILES.map((tile) => (
          <MetricCard
            key={tile.view}
            label={tile.label}
            info={tile.info}
            value={countFor(tile.view).toLocaleString()}
            loading={!stats}
          />
        ))}
      </Box>

      <Box sx={{ mt: 2.5 }}>
        <Panel
          title="Definitions"
          subtitle={
            loading
              ? "Loading definitions…"
              : `${visibleNodes.length.toLocaleString()} ${visibleNodes.length === 1 ? "definition" : "definitions"}${
                  showArchived ? ", deleted ones included" : ""
                }`
          }
          search={<SearchField value={searchQuery} onChange={setSearchQuery} placeholder="Search names and meanings" size="small" />}
          tools={
            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
              <SegmentedControl
                ariaLabel="Where definitions came from"
                value={definitionView}
                options={DEFINITION_VIEWS}
                onChange={setDefinitionView}
              />
              <FormControlLabel
                sx={{ m: 0, "& .MuiFormControlLabel-label": { typography: "body2", color: "text.secondary", ml: 0.75 } }}
                control={<Switch size="small" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />}
                label="Show deleted"
              />
            </Stack>
          }
        >
          <ListTable
            columns={columns}
            rows={visibleNodes}
            getRowKey={(n) => n.node_id}
            onRowClick={openDetail}
            selectedKey={detailOpen ? selectedNode?.node_id ?? null : null}
            loading={loading}
            defaultSort={{ key: "updated", direction: "desc" }}
            pageSize={25}
            pageSizeOptions={[25, 50, 100]}
            emptyMessage={
              searchQuery || definitionView !== "all"
                ? "No definitions match. Try another search or source."
                : "No definitions yet. Add one, or save one from a chat."
            }
          />
        </Panel>
      </Box>

      {/* Detail drawer */}
      <Drawer
        anchor="right"
        open={detailOpen}
        onClose={(_, reason) => {
          if (saving && (reason === "backdropClick" || reason === "escapeKeyDown")) return;
          closeDetail();
        }}
        PaperProps={{ sx: { width: { xs: "100%", sm: 560, md: 600 }, p: { xs: 2, sm: 3 } } }}
      >
        {selectedNode && (
          <DetailPanel
            node={selectedNode}
            changelog={changelog}
            changelogLoading={changelogLoading}
            editing={editing}
            editName={editName}
            editDefinition={editDefinition}
            editFormula={editFormula}
            saving={saving}
            onClose={closeDetail}
            onStartEdit={startEdit}
            onCancelEdit={() => setEditing(false)}
            onSave={saveEdit}
            onArchive={() => requestArchive(selectedNode)}
            setEditName={setEditName}
            setEditDefinition={setEditDefinition}
            setEditFormula={setEditFormula}
          />
        )}
      </Drawer>

      {/* Archive confirm */}
      <ConfirmDialog
        open={archiveConfirmOpen}
        onClose={() => {
          if (!archiveBusy) {
            setArchiveConfirmOpen(false);
            setArchiveTarget(null);
          }
        }}
        onConfirm={() => {
          if (archiveTarget) performArchive(archiveTarget);
        }}
        tone="warning"
        title="Delete definition"
        description={
          archiveTarget
            ? `We'll stop using "${archiveTarget.name}" in every chat. We keep a copy, so you can bring it back any time from "Show deleted".`
            : ""
        }
        confirmLabel="Delete"
        busy={archiveBusy}
      />

      {/* Add definition modal */}
      <AppDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        size="sm"
        title="Add definition"
        busy={addLoading}
        primaryAction={{
          label: "Save",
          onClick: handleAdd,
          disabled: !addName.trim() || !addDefinition.trim(),
        }}
        secondaryAction={{ label: "Cancel", color: "inherit", onClick: () => setAddOpen(false) }}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            size="small"
            value={addName}
            onChange={(e) => setAddName(e.target.value)}
            placeholder="e.g. Net Revenue"
            fullWidth
          />
          <TextField
            label="Definition"
            size="small"
            value={addDefinition}
            onChange={(e) => setAddDefinition(e.target.value)}
            placeholder="How is this calculated or defined?"
            multiline
            rows={3}
            fullWidth
          />
          <TextField
            label="SQL (optional)"
            size="small"
            value={addFormula}
            onChange={(e) => setAddFormula(e.target.value)}
            placeholder="SUM(amount) - SUM(refunds)"
            fullWidth
            sx={{ fontFamily: "monospace" }}
          />
        </Stack>
      </AppDialog>
    </Box>
  );
}

// ============================================================================
// Drawer detail panel
// ============================================================================

function DetailPanel({
  node,
  changelog,
  changelogLoading,
  editing,
  editName,
  editDefinition,
  editFormula,
  saving,
  onClose,
  onStartEdit,
  onCancelEdit,
  onSave,
  onArchive,
  setEditName,
  setEditDefinition,
  setEditFormula,
}: {
  node: KnowledgeNode;
  changelog: ChangelogEntry[];
  changelogLoading: boolean;
  editing: boolean;
  editName: string;
  editDefinition: string;
  editFormula: string;
  saving: boolean;
  onClose: () => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: () => void;
  onArchive: () => void;
  setEditName: (v: string) => void;
  setEditDefinition: (v: string) => void;
  setEditFormula: (v: string) => void;
}) {
  const theme = useTheme();
  const isMobileDetail = useMediaQuery(theme.breakpoints.down("md"));
  const sourceMeta = SOURCE_META[node.source_type];
  const readOnly = isFromDashboard(node);
  const origin = originLine(node);
  const sqlValue =
    node.content?.sql ||
    node.content?.formula_sql ||
    node.content?.rule_sql ||
    "";
  const sqlLabel = "SQL";

  // cmd/ctrl-S to save while editing.
  React.useEffect(() => {
    if (!editing) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (!saving) onSave();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editing, saving, onSave]);

  // Group changelog entries by day for readable history.
  const groupedChangelog = React.useMemo(() => {
    const groups: { day: string; items: ChangelogEntry[] }[] = [];
    for (const entry of changelog) {
      const day = dayBucket(entry.created_at);
      const last = groups[groups.length - 1];
      if (last && last.day === day) {
        last.items.push(entry);
      } else {
        groups.push({ day, items: [entry] });
      }
    }
    return groups;
  }, [changelog]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {isMobileDetail && (
        <Button
          size="small"
          variant="text"
          startIcon={<ArrowBackRounded sx={{ fontSize: 20 }} />}
          onClick={onClose}
          sx={{
            mb: 1.5,
            alignSelf: "flex-start",
            textTransform: "none",
            fontWeight: 600,
            color: "text.secondary",
            px: 0.5,
            minWidth: 0,
            "&:hover": { bgcolor: "action.hover" },
          }}
          aria-label="Back to knowledge list"
        >
          Back to list
        </Button>
      )}

      {/* Hero: glyph + name + sub-line + action cluster */}
      <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 2 }}>
        <TypeGlyph
          tone="neutral"
          icon={sourceMeta ? <sourceMeta.Icon sx={{ fontSize: 22 }} /> : <LabelRounded sx={{ fontSize: 22 }} />}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {editing ? (
            <TextField
              size="small"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              autoFocus
              fullWidth
              sx={{ mb: 0.5 }}
            />
          ) : (
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25, wordBreak: "break-word" }}>
              {node.name}
            </Typography>
          )}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{ mt: 0.5, color: "text.secondary" }}
          >
            {sourceMeta && (
              <Stack direction="row" spacing={0.4} alignItems="center">
                <Box
                  sx={{
                    width: 3,
                    height: 3,
                    borderRadius: "50%",
                    bgcolor: "text.disabled",
                  }}
                />
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <sourceMeta.Icon sx={{ fontSize: 14 }} />
                  <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                    {sourceMeta.label}
                  </Typography>
                </Stack>
              </Stack>
            )}
          </Stack>
          {origin && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              {origin}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {editing ? (
            <>
              <Button
                size="small"
                variant="contained"
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveRounded sx={{ fontSize: 16 }} />}
                onClick={onSave}
                disabled={saving}
                sx={{ textTransform: "none", boxShadow: "none", "&:hover": { boxShadow: "none" }, height: 30 }}
              >
                Save
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={onCancelEdit}
                disabled={saving}
                sx={{ textTransform: "none", height: 30 }}
              >
                Cancel
              </Button>
            </>
          ) : readOnly ? null : (
            <Tooltip title="Edit">
              <IconButton size="small" onClick={onStartEdit} aria-label="Edit definition">
                <EditRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Close">
            <span>
              <IconButton
                size="small"
                onClick={onClose}
                disabled={saving}
                aria-label="Close"
              >
                <CloseRounded fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Stat strip — key timestamps */}
      {!editing && (
        <Box
          sx={(t) => ({
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 1.25,
            p: 1.25,
            borderRadius: `${t.ds.radius.md}px`,
            border: `1px solid ${t.tokens.border.subtle}`,
            bgcolor: t.tokens.surface.raised,
            mb: 2,
          })}
        >
          <DrawerStat label="Created" tooltip={`${formatAbsolute(node.created_at)} · ${createdBy(node)}`}>
            <Typography variant="body2" sx={{ fontSize: "0.8125rem", fontWeight: 600, overflowWrap: "anywhere" }}>
              {formatCalendarDate(node.created_at)} · {createdBy(node)}
            </Typography>
          </DrawerStat>
          <DrawerStat
            label="Updated"
            tooltip={node.updated_at ? formatAbsolute(node.updated_at) : ""}
          >
            <Typography variant="body2" sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>
              {node.updated_at ? relativeTime(node.updated_at) : "—"}
            </Typography>
          </DrawerStat>
        </Box>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Content - scrollable */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {/* Definition */}
        <SectionTitle>Definition</SectionTitle>
        {editing ? (
          <TextField
            size="small"
            value={editDefinition}
            onChange={(e) => setEditDefinition(e.target.value)}
            multiline
            rows={3}
            fullWidth
            sx={{ mb: 2 }}
          />
        ) : (
          <Typography variant="body2" sx={{ mb: 2, whiteSpace: "pre-wrap", lineHeight: 1.55 }}>
            {node.content?.description || (
              <Box component="span" sx={{ color: "text.disabled", fontStyle: "italic" }}>
                No definition provided.
              </Box>
            )}
          </Typography>
        )}

        {/* SQL */}
        {(editing || sqlValue) && (
          <>
            <SectionTitle>{sqlLabel}</SectionTitle>
            {editing ? (
              <TextField
                size="small"
                value={editFormula}
                onChange={(e) => setEditFormula(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                placeholder={
                  node.node_type === "business_rule"
                    ? "status = 'confirmed' AND created_at > NOW() - INTERVAL '90 days'"
                    : "SUM(amount) - SUM(refunds)"
                }
                sx={{ mb: 2, "& textarea": { fontFamily: "monospace", fontSize: "0.8125rem" } }}
              />
            ) : (
              <Paper
                variant="outlined"
                sx={(t) => ({
                  p: 1.5,
                  mb: 2,
                  borderRadius: `${t.ds.radius.sm}px`,
                  bgcolor: alpha(t.tokens.text.primary, t.palette.mode === "light" ? 0.04 : 0.08),
                })}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.8125rem",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {sqlValue}
                </Typography>
              </Paper>
            )}
          </>
        )}

        {/* Subtle hint about cmd/ctrl-S while editing */}
        {editing && !saving && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: "block", mb: 2, fontSize: "0.75rem" }}
          >
            Tip — press ⌘S / Ctrl+S to save.
          </Typography>
        )}

        {/* Changelog (read-only mode only) */}
        {!editing && (
          <>
            <SectionTitle icon={<HistoryRounded sx={{ fontSize: 16 }} />}>History</SectionTitle>
            {changelogLoading ? (
              <LinearProgress sx={{ mb: 2 }} />
            ) : changelog.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem", mb: 2 }}>
                No history yet — this definition hasn't been edited.
              </Typography>
            ) : (
              <Stack spacing={1.5} sx={{ mb: 2 }}>
                {groupedChangelog.map((group) => (
                  <Box key={group.day}>
                    <Typography
                      variant="overline"
                      color="text.disabled"
                      sx={{ fontSize: "0.6875rem", letterSpacing: 0.8, display: "block", mb: 0.5 }}
                    >
                      {group.day}
                    </Typography>
                    <Stack spacing={0.75}>
                      {group.items.map((entry) => (
                        <HistoryEntry key={entry.changelog_id} entry={entry} />
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </>
        )}
      </Box>

      {/* Bottom action */}
      {!editing && readOnly && (
        <Box sx={{ pt: 2, borderTop: 1, borderColor: "divider" }}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
            Change this in the dashboard. We read it from there, so it always matches what the dashboard shows.
          </Typography>
        </Box>
      )}
      {!editing && !readOnly && (
        <Box sx={{ pt: 2, borderTop: 1, borderColor: "divider" }}>
          <Button
            variant="outlined"
            color={node.archived_at ? "primary" : "error"}
            size="small"
            startIcon={node.archived_at ? <UnarchiveRounded /> : <ArchiveRounded />}
            onClick={onArchive}
            fullWidth
            sx={{ textTransform: "none" }}
          >
            {node.archived_at ? "Restore" : "Delete definition"}
          </Button>
          {!node.archived_at && (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: "block", mt: 0.75, fontSize: "0.75rem", textAlign: "center" }}
            >
              We keep deleted definitions, but don’t use them in any chat.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

// ============================================================================
// Drawer subcomponents
// ============================================================================

function TypeGlyph({ tone, icon }: { tone: Tone; icon: React.ReactElement }) {
  return (
    <Box
      aria-hidden
      sx={(t) => {
        const tonePalette: Record<Tone, string> = {
          info: t.tokens.semantic.info,
          warning: t.tokens.semantic.warning,
          success: t.tokens.semantic.success,
          neutral: t.tokens.text.secondary,
        };
        const c = tonePalette[tone];
        return {
          width: 40,
          height: 40,
          borderRadius: `${t.ds.radius.sm}px`,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: c,
          backgroundColor: alpha(c, 0.12),
          border: `1px solid ${alpha(c, 0.2)}`,
        };
      }}
    >
      {icon}
    </Box>
  );
}

function DrawerStat({
  label,
  tooltip,
  children,
}: {
  label: string;
  tooltip?: string;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Tooltip title={tooltip || ""} placement="top" arrow>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{
            fontSize: "0.6875rem",
            letterSpacing: 0.8,
            display: "block",
            mb: 0.5,
            cursor: tooltip ? "help" : "default",
          }}
        >
          {label}
        </Typography>
      </Tooltip>
      {children}
    </Box>
  );
}

function HistoryEntry({ entry }: { entry: ChangelogEntry }) {
  const summaries = summarizeChange(entry);
  return (
    <Paper
      variant="outlined"
      sx={(t) => ({
        p: 1.25,
        borderRadius: `${t.ds.radius.sm}px`,
        bgcolor: t.tokens.surface.raised,
      })}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.25 }}>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {summaries[0]}
        </Typography>
        <Tooltip title={formatAbsolute(entry.created_at)}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
            {relativeTime(entry.created_at)}
          </Typography>
        </Tooltip>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
        by {formatActor(entry.actor_type)}
      </Typography>
      {summaries.slice(1).map((s, i) => (
        <Typography
          key={i}
          variant="caption"
          display="block"
          sx={{ mt: 0.25, fontSize: "0.75rem" }}
        >
          {s}
        </Typography>
      ))}
    </Paper>
  );
}

// ============================================================================
// Small reusable components
// ============================================================================

function SectionTitle({ children, icon }: { children: React.ReactNode; icon?: React.ReactElement }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.75 }}>
      {icon}
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ fontSize: "0.6875rem", letterSpacing: 1 }}
      >
        {children}
      </Typography>
    </Stack>
  );
}
